// ABOUTME: Marca de agua "Dreadlove" sobre la foto final: elige la esquina inferior con menos
// ABOUTME: pelo (según la máscara) y dibuja el logo con sombra suave, sin tapar el peinado.

import QRCode from "qrcode";

export type Corner = "bottom-left" | "bottom-right";

/** Cuenta píxeles de pelo en la franja inferior (tercio) de cada mitad y elige la más libre. */
export function pickWatermarkCorner(hairMask: Uint8Array, width: number, height: number): Corner {
  const y0 = Math.floor((height * 2) / 3);
  let left = 0;
  let right = 0;
  for (let y = y0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (!hairMask[y * width + x]) continue;
      if (x < width / 2) left++;
      else right++;
    }
  }
  return right <= left ? "bottom-right" : "bottom-left";
}

let logoPromise: Promise<HTMLImageElement> | null = null;
function loadLogo(): Promise<HTMLImageElement> {
  if (!logoPromise) {
    logoPromise = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error("No se pudo cargar el logo"));
      img.src = "/brand/logo-white.png";
    });
  }
  return logoPromise;
}

/**
 * Dibuja el logo blanco con sombra en la esquina elegida (ancho ≈ 28% de la foto) y, debajo,
 * una línea con el color o modelo y la web: la foto compartida hace publicidad del producto.
 */
export async function drawWatermark(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  corner: Corner,
  label?: string,
  qrUrl?: string,
): Promise<void> {
  const logo = await loadLogo();
  const w = Math.round(width * 0.28);
  const h = Math.round((w * logo.height) / logo.width);
  const margin = Math.round(width * 0.04);
  const fontPx = Math.max(12, Math.round(width * 0.03));
  const textBlock = label ? fontPx * 1.5 : 0;
  const x = corner === "bottom-right" ? width - w - margin : margin;
  const y = height - h - margin - textBlock;
  ctx.save();
  ctx.globalAlpha = 0.92;
  ctx.shadowColor = "rgba(0,0,0,0.55)";
  ctx.shadowBlur = Math.max(4, Math.round(width * 0.012));
  ctx.shadowOffsetY = Math.round(width * 0.003);
  ctx.drawImage(logo, x, y, w, h);
  if (label) {
    try {
      await document.fonts.load(`600 ${fontPx}px Lexend`);
    } catch {
      /* fuente del sistema */
    }
    ctx.font = `600 ${fontPx}px Lexend, system-ui, sans-serif`;
    ctx.fillStyle = "#ffffff";
    ctx.textBaseline = "alphabetic";
    ctx.textAlign = corner === "bottom-right" ? "right" : "left";
    const tx = corner === "bottom-right" ? width - margin : margin;
    ctx.fillText(label, tx, height - margin);
  }
  ctx.restore();
  if (qrUrl) await drawQr(ctx, width, height, corner === "bottom-right" ? "bottom-left" : "bottom-right", qrUrl);
}

/** QR con la URL del producto en la esquina opuesta, sobre una tarjeta blanca para que se lea. */
async function drawQr(ctx: CanvasRenderingContext2D, width: number, height: number, corner: Corner, url: string): Promise<void> {
  const size = Math.round(width * 0.17);
  const pad = Math.round(size * 0.08);
  const margin = Math.round(width * 0.04);
  const qr = document.createElement("canvas");
  await QRCode.toCanvas(qr, url, { width: size, margin: 0, errorCorrectionLevel: "M", color: { dark: "#461272", light: "#ffffff" } });
  const x = corner === "bottom-right" ? width - size - pad * 2 - margin : margin;
  const y = height - size - pad * 2 - margin;
  ctx.save();
  ctx.shadowColor = "rgba(0,0,0,0.35)";
  ctx.shadowBlur = Math.max(4, Math.round(width * 0.012));
  ctx.fillStyle = "#ffffff";
  const r = Math.round(size * 0.1);
  ctx.beginPath();
  ctx.roundRect(x, y, size + pad * 2, size + pad * 2, r);
  ctx.fill();
  ctx.shadowColor = "transparent";
  ctx.drawImage(qr, x + pad, y + pad, size, size);
  ctx.restore();
}
